import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import styles from "../styles/pages/Dashboard.module.css";
import DashboardFilters from "../components/dashboard/DashboardFilters";
import RecentMessages from "../components/dashboard/RecentMessages";
import { useAuth } from "../contexts/AuthContext";
import { dashboardService } from "../services/dashboard.service";

function toOrdinal(n) {
  const map = { 1:"ro", 2:"do", 3:"ro", 4:"to", 5:"to", 6:"to", 7:"mo", 8:"vo", 9:"no" };
  return `${n}${map[n] ?? "mo"}`;
}
function rankMedalVariant(rank) {
  if (rank === 1) return "gold";
  if (rank === 2) return "silver";
  if (rank === 3) return "bronze";
  return "dark";
}

const SEP_STYLE = {
  gold:  styles.rankingSepGold,
  green: styles.rankingSepGreen,
  teal:  styles.rankingSepTeal,
};

function getBannerInfo(userRank, entries) {
  const user  = entries.find(r => r.rank === userRank);
  const above = entries.find(r => r.rank === userRank - 1);
  const diff  = above ? above.score - user.score : 0;
  if (userRank === 1) return { msg: "¡Felicitaciones! Estás primero 🎉",                              style: "gold"  };
  if (userRank === 2) return { msg: `¡Estás segundo! A sólo ${diff} leads de ser el primero`,         style: "green" };
  if (userRank === 3) return { msg: `¡Estás en el top 3! A sólo ${diff} leads del segundo puesto`,    style: "green" };
  if (userRank === 4) return { msg: `Estás a sólo ${diff} leads del top 3`,                            style: "teal"  };
  return               { msg: `Estás a sólo ${diff} leads del ${toOrdinal(userRank - 1)} puesto`,      style: "teal"  };
}

function _startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function _endOfMonth(d)   { return new Date(d.getFullYear(), d.getMonth() + 1, 0); }

function buildStatCards(s) {
  const pct = s.leads_totales.change_pct ?? 0;
  return [
    {
      id: "leads-totales",
      label: "Leads Totales",
      value: (s.leads_totales.value ?? 0).toLocaleString("es-AR"),
      badge: {
        text: `${Math.abs(pct).toFixed(1)}% ${pct >= 0 ? "↗" : "↘"}`,
        positive: pct >= 0,
      },
      sparkline: s.leads_totales.sparkline ?? [],
    },
    {
      id: "leads-respondidos",
      label: "Leads Respondidos",
      value: (s.leads_respondidos.value ?? 0).toLocaleString("es-AR"),
      badge: null,
      progress: s.leads_respondidos.pct ?? 0,
    },
    {
      id: "ratio-conversion",
      label: "Ratio de Conversión",
      value: (s.ratio_conversion.value ?? 0).toLocaleString("es-AR"),
      badge: null,
      progress: s.ratio_conversion.pct ?? 0,
    },
  ];
}

function getInitials(name = "") {
  return name.split(" ").slice(0, 2).map(w => w[0] ?? "").join("").toUpperCase();
}

function computeNiceTicks(maxVal, targetCount = 5) {
  const rawStep = maxVal / targetCount;
  const mag = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const norm = rawStep / mag;
  const nice = norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 5 ? 5 : 10;
  const step = nice * mag;
  const maxTick = Math.ceil(maxVal / step) * step;
  const count = Math.round(maxTick / step);
  return { ticks: Array.from({ length: count + 1 }, (_, i) => i * step), scale: maxTick };
}

function Sparkline({ data, width = 110, height = 48 }) {
  if (!data || data.length < 2) return null;

  const PAD_TOP    = 4;
  const PAD_BOTTOM = 8;
  const PAD_LEFT   = 2;
  const PAD_RIGHT  = 8;

  const innerW = width  - PAD_LEFT  - PAD_RIGHT;
  const innerH = height - PAD_TOP   - PAD_BOTTOM;

  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;

  const pts = data.map((v, i) => ({
    x: PAD_LEFT  + (i / (data.length - 1)) * innerW,
    y: PAD_TOP   + innerH - ((v - min) / range) * innerH,
  }));

  let d = `M ${pts[0].x} ${pts[0].y}`;
  for (let i = 1; i < pts.length; i++) {
    const cp = (pts[i - 1].x + pts[i].x) / 2;
    d += ` C ${cp} ${pts[i - 1].y}, ${cp} ${pts[i].y}, ${pts[i].x} ${pts[i].y}`;
  }

  const last = pts[pts.length - 1];
  const color = "var(--surface-accent, #359fbb)";

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      style={{ display: "block", flexShrink: 0 }}
      aria-hidden="true"
    >

      <path d={d} fill="none" stroke={color} strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" />

      <line
        x1={last.x} y1={last.y + 5}
        x2={last.x} y2={height - 2}
        stroke={color}
        strokeWidth={1.5}
        strokeDasharray="3 2.5"
        strokeLinecap="round"
      />

      <circle cx={last.x} cy={last.y} r={3.5} fill={color} />
    </svg>
  );
}

function CircularProgress({ size = 52, strokeWidth = 5, percent = 35 }) {
  const radius = (size - strokeWidth) / 2;
  const circ   = 2 * Math.PI * radius;
  const offset = circ - (percent / 100) * circ;

  return (
    <div className={styles.circularProgress} style={{ width: size, height: size }}>
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        style={{ transform: "rotate(-90deg)", display: "block" }}
        aria-hidden="true"
      >
        <circle
          cx={size / 2} cy={size / 2} r={radius}
          fill="none"
          stroke="var(--color-grey-250, #efefef)"
          strokeWidth={strokeWidth}
        />
        <circle
          cx={size / 2} cy={size / 2} r={radius}
          fill="none"
          stroke="var(--surface-accent, #359fbb)"
          strokeWidth={strokeWidth}
          strokeDasharray={circ}
          strokeDashoffset={offset}
          strokeLinecap="round"
        />
      </svg>

      <div className={styles.circularProgressLabel}>{percent}%</div>
    </div>
  );
}

function GaugeChart({ percent = 35, mediaPct = 51 }) {
  const W       = 220;
  const H       = 128;
  const cx      = W / 2;
  const cy      = H;
  const r       = 94;
  const strokeW = 10;

  function polarToCartesian(angleDeg) {
    const rad = (angleDeg * Math.PI) / 180;
    return {
      x: cx + r * Math.cos(Math.PI - rad),
      y: cy - r * Math.sin(Math.PI - rad),
    };
  }

  function describeArc(startDeg, endDeg) {
    const s = polarToCartesian(startDeg);
    const e = polarToCartesian(endDeg);
    const largeArc = endDeg - startDeg > 180 ? 1 : 0;
    return `M ${s.x} ${s.y} A ${r} ${r} 0 ${largeArc} 1 ${e.x} ${e.y}`;
  }

  const userDeg  = (percent  / 100) * 180;
  const mediaDeg = (mediaPct / 100) * 180;

  const leftX  = cx - r;
  const rightX = cx + r;

  return (
    <div className={styles.gaugeWrapper}>
      <svg className={styles.gaugeSvg} viewBox={`0 0 ${W} ${H}`}>

        <path
          d={describeArc(0, 180)}
          fill="none"
          stroke="var(--gauge-track, rgba(0,0,0,0.07))"
          strokeWidth={strokeW}
          strokeLinecap="round"
        />

        <path
          d={describeArc(0, mediaDeg)}
          fill="none"
          stroke="var(--gauge-media, rgba(53,159,187,0.38))"
          strokeWidth={strokeW}
          strokeLinecap="round"
        />

        <path
          d={describeArc(0, userDeg)}
          fill="none"
          stroke="var(--surface-accent, #359fbb)"
          strokeWidth={strokeW}
          strokeLinecap="round"
        />
      </svg>

      <div className={styles.gaugeCenter}>
        <div className={styles.gaugePercent}>{percent}%</div>
      </div>

      <span className={styles.gaugeMinLeft}  style={{ left: leftX  }}>00</span>
      <span className={styles.gaugeMinRight} style={{ left: rightX }}>100</span>
    </div>
  );
}

function StatCard({ card }) {
  return (
    <div className={styles.statCard}>

      <div className={styles.statCardMain}>
        <div className={styles.statValue}>{card.value}</div>
        <div className={styles.statBottom}>
          <span className={styles.statLabel}>{card.label}</span>
          {card.badge && (
            <span className={[
              styles.statBadge,
              card.badge.positive === false ? styles.statBadgeNegative : "",
            ].filter(Boolean).join(" ")}>
              {card.badge.text}
            </span>
          )}
        </div>
      </div>

      {card.sparkline && (
        <div className={styles.statCardRight}>
          <Sparkline data={card.sparkline} width={110} height={52} />
        </div>
      )}
      {card.progress != null && (
        <div className={styles.statCardRight}>
          <CircularProgress percent={card.progress} />
        </div>
      )}
    </div>
  );
}

function GaugeCard({ userPct, mediaPct }) {
  const diff = mediaPct - userPct;

  const subtitle =
    diff > 0
      ? `Te falta un ${diff}% para alcanzar la tasa de respuesta media.`
      : diff === 0
        ? "Alcanzaste la tasa de respuesta media."
        : "¡Felicitaciones! Estás por encima de la tasa de respuesta media.";
  return (
    <div className={styles.gaugeCard}>
      <GaugeChart percent={userPct} mediaPct={mediaPct} />
      <div className={styles.gaugeTitle}>Tasa de respuesta</div>
      <div className={styles.gaugeSubtitle}>
        {subtitle}
      </div>
    </div>
  );
}

const MEDAL_CLASS = {
  gold:   styles.medalGold,
  silver: styles.medalSilver,
  bronze: styles.medalBronze,
  dark:   styles.medalDark,
};

function MedalBadge({ label, variant }) {
  return (
    <span className={[styles.medalBadge, MEDAL_CLASS[variant]].join(" ")}>
      {label}
    </span>
  );
}

function RankingRow({ row, isCurrentUser = false }) {
  return (
    <div className={[styles.rankingRow, isCurrentUser ? styles.rankingRowCurrent : ""].filter(Boolean).join(" ")}>
      <div className={styles.rankingLeft}>
        <MedalBadge label={toOrdinal(row.rank)} variant={rankMedalVariant(row.rank)} />
        <div className={styles.rankingAvatar}>{row.initials}</div>
        <span className={styles.rankingName}>{row.name}</span>
      </div>
      <span className={styles.rankingScore}>{row.score}</span>
    </div>
  );
}

function RankingCard({ entries, currentUserRank }) {

  const rows   = [...entries].sort((a, b) => a.rank - b.rank);
  const banner = currentUserRank != null ? getBannerInfo(currentUserRank, entries) : null;

  return (
    <div className={styles.rankingCard}>
      <div className={styles.rankingHeader}>
        <span className={styles.rankingTitle}>Ranking</span>
        <span className={styles.rankingColHeader}>Leads Respondidas</span>
      </div>

      <div className={styles.rankingScroll}>
        {rows.map(row => {
          const isCurrentUser = row.rank === currentUserRank;
          return (
            <React.Fragment key={row.rank}>
              <RankingRow row={row} isCurrentUser={isCurrentUser} />

              {isCurrentUser && banner && (
                <div className={[styles.rankingSeparator, SEP_STYLE[banner.style]].join(" ")}>
                  {banner.msg}
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

function BarChartCard({ barData: data }) {
  const [hoveredLabel, setHoveredLabel] = useState(null);

  const maxVal = Math.max(...data.map(d => d.value), 1);
  const { ticks: xTicks, scale: barScale } = useMemo(
    () => computeNiceTicks(maxVal, 8),
    [maxVal],
  );

  return (
    <div className={styles.barChartCard}>
      <div className={styles.barChartTitle}>Leads Totales por Estado</div>

      <div className={styles.barChartBody}>

        <div className={styles.barGridOverlay}>
          {xTicks.slice(1).map(tick => (
            <div
              key={tick}
              className={styles.barGridLine}
              style={{ left: `${(tick / barScale) * 100}%` }}
            />
          ))}
        </div>

        <div className={styles.barRows}>
          {data.map((row) => {
            const pct     = Math.round((row.value / barScale) * 100);
            const isHover = hoveredLabel === row.label;

            const markerLeft = `max(4px, calc(${pct}% - 20px))`;

            const fillClass = [
              styles.barFill,
              isHover ? styles.barFillHover : "",
            ].filter(Boolean).join(" ");

            return (
              <div
                key={row.label}
                className={styles.barRow}
                onMouseEnter={() => setHoveredLabel(row.label)}
                onMouseLeave={() => setHoveredLabel(null)}
              >
                <div className={styles.barLabel}>{row.label}</div>

                <div className={styles.barTrackOuter}>

                  <div className={styles.barTrack}>
                    <div className={fillClass} style={{ width: `${pct}%` }} />
                  </div>

                  {isHover && (
                    <>
                      <div className={styles.barPulseWrapper} style={{ left: markerLeft }}>
                        <div className={styles.barPulseRing} />
                        <div className={styles.barPulseDot} />
                      </div>
                      <div className={styles.barBubble} style={{ left: markerLeft }}>
                        {row.value.toLocaleString("es-AR")}
                      </div>
                    </>
                  )}
                </div>

                <div className={styles.barValueExternal} />
              </div>
            );
          })}
        </div>
      </div>

      <div className={styles.barXAxisRow}>
        <div className={styles.barXAxisSpacer} />
        <div className={styles.barXAxis}>
          {xTicks.map(t => (
            <span key={t} className={styles.barXLabel}>{t}</span>
          ))}
        </div>
        <div className={styles.barXAxisEnd} />
      </div>
    </div>
  );
}

function SectionError({ label }) {
  return (
    <div className={styles.sectionError}>
      <span className={styles.sectionErrorIcon}>!</span>
      <span>No se pudo cargar <strong>{label}</strong>.</span>
    </div>
  );
}

export default function DashboardPage() {
  const { auth } = useAuth();
  const navigate = useNavigate();

  const userId = auth?.user?.id ?? null;

  const [filterAccounts, setFilterAccounts] = useState([]);
  const [filterTypes,    setFilterTypes]    = useState([]);
  const [filterDate, setFilterDate] = useState({
    startDate: _startOfMonth(new Date()),
    endDate:   _endOfMonth(new Date()),
  });

  const [summary,      setSummary]      = useState(null);
  const [rankingData,  setRankingData]  = useState(null);
  const [stagesData,   setStagesData]   = useState(null);
  const [messagesData, setMessagesData] = useState(null);
  const [summaryErr,   setSummaryErr]   = useState(null);
  const [rankingErr,   setRankingErr]   = useState(null);
  const [stagesErr,    setStagesErr]    = useState(null);
  const [messagesErr,  setMessagesErr]  = useState(null);
  const [loading, setLoading] = useState(false);

  const filters = useMemo(() => ({
    from:     filterDate.startDate,
    to:       filterDate.endDate,
    accounts: filterAccounts,
    types:    filterTypes,
  }), [filterDate, filterAccounts, filterTypes]);

  const handleOpenLead = (message) => {
    if (message?.lead_id == null) return;
    navigate(`/leads/${message.lead_id}`);
  };

  const handleOpenConversation = (message) => {
    if (message?.lead_id == null) return;
    navigate(`/leads/${message.lead_id}`, {
      state: { openSubjectGroupId: message.subject_group_id ?? null },
    });
  };

  useEffect(() => {
    if (!userId) return;

    let cancelled = false;
    const controller = new AbortController();
    const { signal } = controller;

    setLoading(true);
    setSummaryErr(null);
    setRankingErr(null);
    setStagesErr(null);
    setMessagesErr(null);

    Promise.allSettled([
      dashboardService.summary(filters, { signal }),
      dashboardService.ranking(filters, { signal }),
      dashboardService.leadsByStage(filters, { signal }),
      dashboardService.messages(filters, { signal }),
    ]).then(([sumRes, rankRes, stgRes, msgRes]) => {
      if (cancelled) return;

      setSummary(sumRes.status    === "fulfilled" ? sumRes.value    : null);
      setSummaryErr(sumRes.status === "rejected"  ? sumRes.reason   : null);

      setRankingData(rankRes.status === "fulfilled" ? rankRes.value  : null);
      setRankingErr(rankRes.status  === "rejected"  ? rankRes.reason : null);

      setStagesData(stgRes.status === "fulfilled" ? stgRes.value   : null);
      setStagesErr(stgRes.status  === "rejected"  ? stgRes.reason  : null);

      setMessagesData(msgRes.status === "fulfilled" ? msgRes.value   : null);
      setMessagesErr(msgRes.status  === "rejected"  ? msgRes.reason  : null);
    }).finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; controller.abort(); };
  }, [filters, userId]);

  const statCards       = summary ? buildStatCards(summary) : null;
  const gaugePcts       = summary
    ? { user: summary.tasa_respuesta.user_pct, media: summary.tasa_respuesta.media_pct }
    : null;
  const rankingEntries  = rankingData?.entries?.map(e => ({
    ...e, initials: e.initials ?? getInitials(e.name),
  })) ?? null;
  const currentUserRank = rankingData
    ? rankingData.entries.find(e => String(e.user_id) === String(rankingData.current_user_id))?.rank ?? null
    : null;
  const barDataResolved = stagesData?.stages ?? null;
  const recentMessages  = messagesData?.messages ?? null;

  const headerEl = (
    <div className={styles.header}>
      <h1 className={styles.title}>Dashboard</h1>
      <DashboardFilters
        onAccountChange={setFilterAccounts}
        onTypeChange={setFilterTypes}
        onDateChange={({ startDate, endDate }) => setFilterDate({ startDate, endDate })}
      />
    </div>
  );

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.inner}>
          {headerEl}
          <div className={styles.loadingState}>
            <div className={styles.loadingSpinner} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.inner}>
        {headerEl}

        <div className={styles.mainGrid}>

          <div className={styles.leftCol}>
            {summaryErr
              ? <SectionError label="Tasa de respuesta" />
              : gaugePcts && <GaugeCard userPct={gaugePcts.user} mediaPct={gaugePcts.media} />
            }
            {rankingErr
              ? <SectionError label="Ranking" />
              : rankingEntries?.length > 0 &&
                <RankingCard entries={rankingEntries} currentUserRank={currentUserRank} />
            }
          </div>

          <div className={styles.statsRow}>
            {summaryErr
              ? <SectionError label="Estadísticas" />
              : (statCards ?? []).map(c => <StatCard key={c.id} card={c} />)
            }
          </div>

          <div className={styles.rightCol}>
            {stagesErr
              ? <SectionError label="Leads por estado" />
              : barDataResolved && <BarChartCard barData={barDataResolved} />
            }
          </div>
        </div>

        {messagesErr
          ? <SectionError label="Nuevos mensajes" />
          : recentMessages && (
              <RecentMessages
                messages={recentMessages}
                onOpenLead={handleOpenLead}
                onOpenConversation={handleOpenConversation}
              />
            )
        }
      </div>
    </div>
  );
}
